// Import the necessary components
import { at } from 'lodash'
import moment from 'moment'
import VerofreshRecord from './VerofreshRecord'

class VehicleRecord extends VerofreshRecord {
  /**
   * Returns true/false if the vehicle has GPS coordinates set
   * @return boolean
   */
  hasGpsCoordinates () {
    if (this.currentLatitude() != null && this.currentLongitude() != null) {
      return true
    }

    return false
  };

  hasZeroedGpsCoordinates () {
    return (this.currentLatitude() === 0 && this.currentLongitude() === 0)
  }

  /**
   * Returns a comma separated string of latitude and longitude (e.g.
   * 32.123445,-113.987654)
   *
   * @return string
   */
  getGpsCoordinatesAsString () {
    if (this.hasGpsCoordinates()) {
      return `${this.currentLatitude().toFixed(6)},${this.currentLongitude().toFixed(6)}`
    }
    return 'unknown'
  };

  /**
   * Returns the most recent latitude or longitude reading, if neither is set
   * then this method returns undefined
   *
   * @return string | undefined
   */
  getMostRecentGpsReading () {
    let latTime = at(this, 'state.lat.ts')[0]
    let longTime = at(this, 'state.long.ts')[0]

    // Unix epoc time used to identify when we don't have a valid time for
    // lat and/or long
    const invalidTime = moment(0)

    latTime = latTime === undefined ? invalidTime : moment(latTime)
    longTime = longTime === undefined ? invalidTime : moment(longTime)

    const mostRecentTime = moment.max(longTime, latTime)

    // If the most recent time is the same as our invalid time then we don't
    // have a GPS reading and return undefined
    if (mostRecentTime.isSame(invalidTime)) {
      return undefined
    }

    return mostRecentTime.format()
  }

  /**
   * @return float | undefined
   */
  currentLatitude () {
    return at(this, 'state.lat.value')[0]
  }

  /**
   * @return float | undefined
   */
  currentLongitude () {
    return at(this, 'state.long.value')[0]
  }

  /**
   * @return float | undefined
   */
  currentZoneTemp (zone) {
    return at(this, `state.${zone}.value`)[0]
  }

  /**
   * Returns the most recent latitude or longitude reading, if neither is set
   * then this method returns undefined
   *
   * @return string | undefined
   */
  getMostRecentZoneReading (zone) {
    let zoneTime = at(this, `state.${zone}.ts`)[0]

    // Unix epoc time used to identify when we don't have a valid time for
    // lat and/or long
    const invalidTime = moment(0)

    zoneTime = zoneTime === undefined ? invalidTime : moment(zoneTime)

    if (zoneTime.isSame(invalidTime)) {
      return undefined
    }

    return zoneTime.format()
  }

  fetchCustomersRelations () {
    return this._mapper()
      .datastore
      .getMapper('Vehicle')
      .fetchCustomersRelations({
        vehicle_id: this.id
      })
  }

  fetchStates () {
    return this._mapper()
      .datastore
      .getMapper('Vehicle')
      .fetchStates({
        vehicle_id: this.id
      })
  }

  updateCustomers (customers) {
    this.customers = []

    customers.forEach(customerRelation => {
      const customer = this._mapper()
        .datastore
        .getMapper('Customer')
        .createRecord(customerRelation.customer)

      this.customers.push(customer)

      if (customerRelation.primary_customer) {
        this.customer = customer
        this.primary_customer = customer
      }

      if (customerRelation.billing_customer) {
        this.customer = customer
        this.billing_customer = customer
      }
    })
  }

  /**
   *
   * @param int customerId
   * @param boolean primaryCustomer
   *
   * @return promise
   */
  linkCustomer (customerId, primaryCustomer = false) {
    return this._mapper()
      .datastore
      .getMapper('Vehicle')
      .linkCustomer({
        vehicle_id: this.id,
        data: {
          customer_id: customerId,
          vehicle_id: this.id,
          primary_customer: primaryCustomer,
        }
      })
  }

  /**
   *
   * @param int customerId
   *
   * @return promise
   */
  unlinkCustomer (customerId) {
    return this._mapper()
      .datastore
      .getMapper('Vehicle')
      .unlinkCustomer({
        customer_id: customerId,
        vehicle_id: this.id
      })
  }

  setPrimaryCustomer (customerId) {
    return this._mapper()
      .datastore
      .getMapper('Vehicle')
      .updateCustomerRelation({
        customer_id: customerId,
        vehicle_id: this.id,
        data: { primary_customer: true }
      })
  }

  setBillingCustomer (customerId) {
    return this._mapper()
      .datastore
      .getMapper('Vehicle')
      .updateCustomerRelation({
        customer_id: customerId,
        vehicle_id: this.id,
        data: { billing_customer: true }
      })
  }
}

// Actions allow us to defined additional calls we can make to related
// endpoints.
const actions = {
  fetchCustomersRelations: {
    method: 'GET',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.vehicle_id}/customer?expand=customer`
    },
    response (response) {
      return response.data
    }
  },
  linkCustomer: {
    method: 'POST',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.vehicle_id}/customer`
    },
    response (response) {
      return response.data
    }
  },
  unlinkCustomer: {
    method: 'DELETE',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.vehicle_id}/customer/${options.customer_id}`
    },
    response (response) {
      return response.data
    }
  },
  updateCustomerRelation: {
    method: 'PATCH',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.vehicle_id}/customer/${options.customer_id}`
    },
    response (response) {
      return response.data
    }
  }
}

export default VehicleRecord
export { actions }
